import { inject, Injectable, NgZone } from '@angular/core';
import { WindowRefService } from '@callrail/looky/util';
import { Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { take } from 'rxjs/operators';

import { PendoInitOptions } from '../../interfaces/pendo-init-options.interface';
import { PendoLoadedGuide } from '../../interfaces/pendo-loaded-guide.interface';
import { PENDO_API_KEY } from '../../tokens/pendo-api-key.token';

/**
 * Thin, auth-free wrapper around the Pendo agent.
 *
 * Owns the script tag, the visitor/account handshake, and the guide APIs. Knows
 * nothing about the current user, agency, or any HTTP service, so it is safe to
 * inject from unauthenticated apps (anubis) as well as authenticated ones.
 *
 * Authenticated behavior — payload building, guide-to-action mapping, cross-app
 * navigation handlers — lives in `ProductGuideService` in `@callrail/looky/core`,
 * which delegates to this service.
 *
 * Important Restrictions:
 * ✅ Only fire on certain environments: staging
 * ✅ Do NOT send HIPAA sensitive info to Pendo
 */
@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private readonly window = inject(WindowRefService);
  private readonly ngZone = inject(NgZone);
  private readonly apiKey = inject(PENDO_API_KEY);

  private readonly readySubject = new ReplaySubject<void>(1);
  private readonly guidesLoadedSubject = new ReplaySubject<void>(1);
  private readonly guideAdvanceSubject = new Subject<PendoLoadedGuide>();

  private customEventHandler: (event: Event) => void;
  private _pendingGuideId: string;

  /**
   * Fires when Pendo script events are ready. Since it's based on a ReplaySubject, this
   * won't fire subscriptions until `.next()` is called. And once `.next()` is called the
   * first time, it will fire subscriptions immediately as soon as they're subscribed.
   */
  public readonly pendoReady$ = this.readySubject.asObservable().pipe(take(1));

  /**
   * Fires when the Pendo agent has loaded the guides for the current visitor.
   * Note this is the agent's own `guidesLoaded` event, not our database records.
   */
  public readonly guidesLoaded$ = this.guidesLoadedSubject
    .asObservable()
    .pipe(take(1));

  /**
   * Emits the guide from the `_pendo-guide-advance` custom event, which Pendo
   * guides dispatch from a custom code block.
   */
  public readonly guideAdvance$ = this.guideAdvanceSubject.asObservable();

  constructor() {
    if (this.approvedEnvironment) {
      this.loadScript();
    }
  }

  public get approvedEnvironment(): boolean {
    return process.env.NODE_ENV === 'production'; // staging and prod
  }

  public get pendoAvailable(): boolean {
    return (
      (this.approvedEnvironment && !!this.window.nativeWindow().pendo) ||
      this.runInDevelopmentMode
    );
  }

  public get pendingGuideId(): string {
    return this._pendingGuideId;
  }

  public set pendingGuideId(guideId: string) {
    this._pendingGuideId = guideId;
  }

  /**
   * Starts a Pendo session. Wires the agent's `ready` and `guidesLoaded` events
   * into `pendoReady$` / `guidesLoaded$`.
   */
  public initialize(data: PendoInitOptions = {}): void {
    if (this.runInDevelopmentMode) {
      return this.logDevelopmentModeInfo('initialize', data);
    }
    this.pendo?.initialize({
      ...data,
      events: {
        ready: () => this.readySubject.next(),
        guidesLoaded: () => this.onGuidesLoaded(),
      },
    });
  }

  /**
   * Starts a Pendo session with no visitor id, so the agent generates an anonymous
   * visitor id and stores it on the page domain. A later `identify()` from an
   * authenticated app on the same host stitches that anonymous history to the
   * real visitor.
   */
  public initializeAnonymous(data: PendoInitOptions = {}): void {
    const visitor = { ...(data.visitor ?? {}) };
    delete visitor.id;
    this.initialize({ ...data, visitor });
  }

  /**
   * After Pendo has been initialized, identify the visitor/account when they change
   * without a reload rather than initializing again.
   */
  public identify(data: PendoInitOptions): void {
    if (this.runInDevelopmentMode) {
      return this.logDevelopmentModeInfo('identify', data);
    }
    this.pendo?.identify(data);
  }

  /**
   * Partial update of the current visitor/account. Any property not included is
   * left untouched, and no id needs to be passed — Pendo targets the current
   * visitor/account automatically.
   */
  public updateOptions(data: PendoInitOptions): void {
    if (this.runInDevelopmentMode) {
      return this.logDevelopmentModeInfo('updateOptions', data);
    }
    this.pendo?.updateOptions(data);
  }

  /**
   * Subscribes to a Pendo agent event once the agent is ready. The listener is
   * attached outside Angular; the callback is run back inside it.
   */
  public onPendoEvent(
    eventName: string,
    callback: (...args: unknown[]) => void
  ): void {
    this.pendoReady$.subscribe(() => {
      this.ngZone.runOutsideAngular(() => {
        this.pendo?.events?.on(eventName, (...args: unknown[]) => {
          this.ngZone.run(() => callback(...args));
        });
      });
    });
  }

  /*
    Can only show guides where the user is in the target segment and Pendo is ready to show that guide.
  */
  public showGuide(guideId: string): void {
    if (this.guideIsAvailable(guideId)) {
      this.showGuideById(guideId);
    }
  }

  /**
   * Shows a guide without checking that it is loaded and showable. Prefer
   * `showGuide()` unless you specifically need to bypass that check.
   */
  public showGuideById(guideId: string): void {
    this.pendo?.showGuideById(guideId);
  }

  /*
    Checks to see if a particular guide is currently loaded in the pendo enviroment.
    `pendo.guides` only contains guides where a user is in the target segment, and
    Pendo is ready to show that guide to the user.
  */
  public guideIsAvailable(guideId: string): boolean {
    const guide = this.findPendoGuideById(guideId);
    return typeof guide?.canShow === 'function' && guide?.canShow();
  }

  /*
    Guides that have been snoozed (distinct from dismissed) will still be considered available
    in the above method, and are still triggerable via `showGuide()`.
  */
  public guideIsSnoozed(guideId: string): boolean {
    const guide = this.findPendoGuideById(guideId);
    return guide?.isGuideSnoozed();
  }

  public guideVisible(guideId: string): boolean {
    const guide = this.findPendoGuideById(guideId);
    // we're seeing some errors where isShown isn't a function.
    // make sure it's a function before we try to call it.
    return typeof guide?.isShown === 'function' && guide?.isShown();
  }

  public dismiss(): void {
    this.pendo.onGuideDismissed();
  }

  /**
   * Sometimes when in a module in the Resource Center and clicking on the overlay,
   * the RC doesn't close fully when calling onGuideDismissed just once. So let's
   * call it twice!
   */
  public dismissResourceCenter(): void {
    if (this.pendo?.onGuideDismissed) {
      this.pendo.onGuideDismissed();
      this.pendo.onGuideDismissed();
    }
  }

  /**
   * Uses a MutationObserver to return an observable when the passed-in
   * node id has been removed from the DOM. Note that this can fire multiple
   * times when the guide is removed, so plan accordingly. Also the parentNode
   * for a Pendo guide is `body` so it's possible other DOM mutations could trigger this.
   * Only call this method when you know there should be a guide on the page.
   *
   * @param pendoNodeId - The DOM Id used to select the node to watch for changes
   * @param mutationObserver - MutationObserver class. Defaults to the browser MutationObserver.
   */
  public watchForPendoRemoval$(
    pendoNodeId: string,
    mutationObserver = MutationObserver
  ): Observable<void> {
    const targetNode = document.getElementById(pendoNodeId)?.parentNode;
    if (targetNode) {
      const subject = new Subject<void>();
      const mutObserver = new mutationObserver((mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList' && mutation.removedNodes[0]) {
            // Clean up the observer before calling the callback
            observer.takeRecords();
            observer.disconnect();
            subject.next();
            subject.complete();
          }
        }
      });
      mutObserver.observe(targetNode, { childList: true });
      return subject.asObservable();
    }
    return throwError(
      () =>
        `Couldn't find node with id ${pendoNodeId}. Make sure this is an approved environment for Pendo.`
    );
  }

  public watchForPendoAddition$(
    pendoNodeId: string,
    mutationObserver = MutationObserver
  ): Observable<void> {
    const targetNode = document.getElementsByTagName('body')[0] as Node;
    if (targetNode) {
      const subject = new Subject<void>();
      const mutObserver = new mutationObserver((mutationsList, observer) => {
        for (const mutation of mutationsList) {
          const el = mutation.addedNodes[0] as HTMLElement;
          if (mutation.type === 'childList' && el?.id === pendoNodeId) {
            // Clean up the observer before calling the callback
            observer.takeRecords();
            observer.disconnect();
            subject.next();
            subject.complete();
          }
        }
      });
      mutObserver.observe(targetNode, { childList: true });
      return subject.asObservable();
    }
  }

  private get pendo(): any {
    return this.window.nativeWindow().pendo;
  }

  /**
   * Return true to see a log of what we would have sent to pendo
   * instead of having to fully load the pendo script and send your
   * local data to the real third party service
   * (It'll fail the specs if you forget to turn it off)
   */
  private get runInDevelopmentMode(): boolean {
    return false;
  }

  private onGuidesLoaded(): void {
    this.guidesLoadedSubject.next();
    this.triggerPendingGuide();
  }

  private triggerPendingGuide(): void {
    if (
      this.pendingGuideId != null &&
      this.pendo?.showGuideById(this.pendingGuideId)
    ) {
      this.pendingGuideId = null;
    }
  }

  // Searches through guides loaded by pendo
  private findPendoGuideById(guideId: string): PendoLoadedGuide {
    return (this.pendo?.guides || []).find(
      (g: PendoLoadedGuide) => g.id === guideId
    );
  }

  private logDevelopmentModeInfo(method: string, data: PendoInitOptions): void {
    // eslint-disable-next-line no-console
    console.log(`DEVELOPMENT LOG: pendo.${method}`, data);
  }

  private loadScript(): void {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.text = this.pendoSource;

    document.getElementsByTagName('body')[0].appendChild(scriptElement);
    this.addCustomEventListener();
  }

  private addCustomEventListener(): void {
    // Re-emits the _pendo-guide-advance custom event, defined in Pendo guides
    this.customEventHandler = this.handleEvent.bind(this);

    // The click handler needs to be the exact same function reference for the removal to work.
    // In case this method ever gets called more than once, we don't want to create multiple listeners,
    // so remove it if it exists before setting it.
    this.window
      .nativeWindow()
      .removeEventListener('_pendo-guide-advance', this.customEventHandler);
    this.window
      .nativeWindow()
      .addEventListener('_pendo-guide-advance', this.customEventHandler);
  }

  private handleEvent(event: CustomEvent): void {
    if (event?.detail?.guide) {
      this.guideAdvanceSubject.next(event.detail.guide);
    }
  }

  private get pendoSource(): string {
    return `(function(apiKey){
      (function(p,e,n,d,o){
        var v,w,x,y,z;o=p[d]=p[d]||{};o._q=[];
        v=['initialize','identify','updateOptions','pageLoad'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
      })('${this.apiKey}')`;
  }
}
